import { useState } from "react";
import { Plus, Search } from "lucide-react";
import { IconeColorido } from "@/components/comecar/selecao/IconeColorido";
import { iconeDaCategoria } from "@/components/comecar/selecao/icones-da-selecao";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_ORGANIZACAO_E_CONTA } from "@/lib/ajuda/dialogos/organizacao-e-conta";

const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** Escolher uma especialidade pra clínica: as em destaque primeiro, busca pelas outras. */
export function AdicionarEspecialidadeDialog({
  open,
  ativas,
  ocupado,
  onClose,
  onEscolher,
}: {
  open: boolean;
  ativas: string[];
  ocupado?: boolean;
  onClose: () => void;
  onEscolher: (especialidade: { id: string; label: string }) => void;
}) {
  const [busca, setBusca] = useState("");
  const todas = trpc.specialty.list.useQuery(undefined, { enabled: open }).data ?? [];
  const termo = semAcento(busca.trim());
  const livres = todas.filter((e) => !ativas.includes(e.id) && (!termo || semAcento(e.label).includes(termo)));
  const grupos = [
    { titulo: "Mais comuns", itens: livres.filter((e) => e.destaque) },
    { titulo: "Outras especialidades", itens: livres.filter((e) => !e.destaque) },
  ].filter((g) => g.itens.length);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 border border-zinc-200 bg-white p-0 shadow-xl sm:max-w-lg dark:border-border dark:bg-card">
        <DialogHeader className="border-b border-zinc-100 px-5 py-4 text-left dark:border-border">
          <DialogTitle ajuda={AJUDA_ORGANIZACAO_E_CONTA.adicionarEspecialidade}>Adicionar especialidade</DialogTitle>
          <DialogDescription>Em seguida você escolhe os procedimentos dela que a clínica faz.</DialogDescription>
        </DialogHeader>
        <div className="border-b border-zinc-100 px-5 py-3 dark:border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar especialidade..."
              className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-border dark:bg-card"
            />
          </div>
        </div>
        <div className="min-h-[160px] flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {grupos.length === 0 && (
            <p className="py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhuma especialidade encontrada.</p>
          )}
          {grupos.map((g) => (
            <section key={g.titulo}>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">{g.titulo}</p>
              <div className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
                {g.itens.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    disabled={ocupado}
                    onClick={() => onEscolher(e)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-zinc-900 hover:bg-zinc-50 disabled:opacity-60 dark:text-foreground dark:hover:bg-muted"
                  >
                    <IconeColorido {...iconeDaCategoria(e.label)} tamanho="sm" />
                    <span className="min-w-0 flex-1">{e.label}</span>
                    <Plus className="h-4 w-4 shrink-0 text-zinc-400" />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
